const PRODUCER_KEY_ALIAS = "angolabi.producer.default";

function canUseWindow() {
  return typeof window !== "undefined";
}

function isTauriDesktop() {
  return canUseWindow() && Boolean(window.__TAURI_INTERNALS__);
}

async function getTauriInvoke() {
  if (!isTauriDesktop()) {
    return null;
  }

  try {
    const core = await import("@tauri-apps/api/core");
    return core?.invoke || null;
  } catch {
    return null;
  }
}

async function invokeLocalVault(command, payload = {}) {
  const invoke = await getTauriInvoke();
  if (!invoke) {
    return null;
  }

  return invoke(command, payload);
}

export function buildContributorKeyAlias(companyUuid) {
  return `angolabi.contribuinte.${String(companyUuid || "").trim()}`;
}

export function getProducerKeyAlias() {
  return PRODUCER_KEY_ALIAS;
}

export async function ensureBundledProducerKeyProvisioned() {
  return invokeLocalVault("local_vault_ensure_bundled_producer_key");
}

export async function storeLocalVaultPrivateKey({
  alias,
  kind,
  privateKey,
  scope,
  companyUuid,
  storeUuid,
  source,
  metadata,
}) {
  if (!alias || !privateKey) {
    return null;
  }

  return invokeLocalVault("local_vault_put_private_key", {
    alias,
    kind,
    privateKey,
    scope: scope || null,
    companyUuid: companyUuid || null,
    storeUuid: storeUuid || null,
    source: source || null,
    metadata: metadata || null,
  });
}

export async function getLocalVaultStatus(alias) {
  if (!alias) {
    return null;
  }

  return invokeLocalVault("local_vault_get_status", { alias });
}

export async function removeLocalVaultPrivateKey(alias) {
  if (!alias) {
    return null;
  }

  return invokeLocalVault("local_vault_remove_private_key", { alias });
}

export async function signJsonWithLocalVault({ alias, payload, headers }) {
  if (!alias || !payload) {
    return null;
  }

  return invokeLocalVault("local_vault_sign_json", {
    alias,
    payload,
    headers: headers || null,
  });
}

export async function signJsonWithPreferredLocalVault({ companyUuid, payload, headers, includeProducerFallback = true }) {
  const aliases = [];
  if (companyUuid) {
    aliases.push(buildContributorKeyAlias(companyUuid));
  }
  if (includeProducerFallback) {
    aliases.push(PRODUCER_KEY_ALIAS);
  }

  let lastError = null;
  for (const alias of aliases) {
    try {
      const result = await signJsonWithLocalVault({ alias, payload, headers });
      if (result) {
        return result;
      }
    } catch (error) {
      lastError = error;
    }
  }

  if (lastError) {
    throw lastError;
  }

  return null;
}

export async function storeSecureJsonInLocalVault({ alias, kind, value, scope, source, metadata }) {
  if (!alias) {
    return null;
  }

  const normalizedValue = typeof value === "string" ? value : JSON.stringify(value ?? null);

  return invokeLocalVault("local_secure_store_json", {
    alias,
    kind: kind || "generic",
    valueJson: normalizedValue,
    scope: scope || null,
    source: source || null,
    metadata: metadata || null,
  });
}

export async function getSecureJsonFromLocalVault(alias) {
  if (!alias) {
    return null;
  }

  return invokeLocalVault("local_secure_get_json", { alias });
}

export async function removeSecureJsonFromLocalVault(alias) {
  if (!alias) {
    return null;
  }

  return invokeLocalVault("local_secure_remove_json", { alias });
}